import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  OutputNodeDefinition,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowParameterValueErrorType
} from '../types'
import {
  buildJoinColumnList,
  buildJoinOutputSchema,
  validateAtLeastOneColumnSelected,
  validateJoinKeyColumnTypes
} from '../utils'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class JoinV2 extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.joinv2',
        group: 'Joins',
        title: 'Join',
        description: `
      This component combines rows from two separate tables based on a related column between them.
      `,
        starred: true
      },
      [
        {
          name: 'lefttable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Left table'
        },
        {
          name: 'righttable',
          type: WorkflowComponentParamType.InputTable,
          title: 'Right table'
        },
        {
          name: 'lefttablekeycolumn',
          type: WorkflowComponentParamType.Column,
          title: 'Key column in left table',
          parent: 'lefttable',
          dataType: [
            ColumnDataType.Boolean,
            ColumnDataType.Date,
            ColumnDataType.Datetime,
            ColumnDataType.Time,
            ColumnDataType.Timestamp,
            ColumnDataType.Number,
            ColumnDataType.String
          ]
        },
        {
          name: 'lefttablecolumns',
          type: WorkflowComponentParamType.ColumnsForJoin,
          title: 'Left table columns',
          parent: 'lefttable',
          optional: true
        },
        {
          name: 'righttablekeycolumn',
          type: WorkflowComponentParamType.Column,
          title: 'Key column in right table',
          parent: 'righttable',
          dataType: [
            ColumnDataType.Boolean,
            ColumnDataType.Date,
            ColumnDataType.Datetime,
            ColumnDataType.Time,
            ColumnDataType.Timestamp,
            ColumnDataType.Number,
            ColumnDataType.String
          ]
        },
        {
          name: 'righttablecolumns',
          type: WorkflowComponentParamType.ColumnsForJoin,
          title: 'Right table columns',
          parent: 'righttable',
          optional: true
        },
        {
          name: 'jointype',
          type: WorkflowComponentParamType.Selection,
          title: 'Join type',
          options: ['Inner', 'Left', 'Right', 'Full outer'],
          default: 'Inner'
        },
        {
          name: 'whereclause',
          type: WorkflowComponentParamType.StringSql,
          title: 'Where clause',
          advanced: true,
          optional: true
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)

    const preparedParams = this._prepareParameters(params)

    const leftTable = preparedParams.lefttable as string
    const rightTable = preparedParams.righttable as string
    const leftTableColumns = preparedParams.lefttablecolumns as string
    const rightTableColumns = preparedParams.righttablecolumns as string
    const leftTableJoinColumn = preparedParams.lefttablekeycolumn as string
    const rightTableJoinColumn = preparedParams.righttablekeycolumn as string
    const joinType = preparedParams.jointype as string
    const whereClause = preparedParams.whereclause as string

    const cols = buildJoinColumnList(leftTableColumns, rightTableColumns, '_left', '_right')
    const colString = cols.join(', ')

    const select = dedent`
        SELECT ${connection.type === Provider.DatabricksRest ? '/*+ REPARTITION(40) */' : ''}
          ${colString}
        FROM
          ${connection.quoteName(leftTable as string)} AS _left
        ${(joinType as string).toUpperCase()} JOIN
          ${connection.quoteName(rightTable as string)} AS _right
        ON
          _left.${leftTableJoinColumn} = _right.${rightTableJoinColumn}
        ${whereClause ? `WHERE ${whereClause}` : ''}
      `
    let createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
    if (connection.type === Provider.DatabricksRest) {
      createTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(createTable)
    }
    return {
      query: createTable,
      outputs: { result: outputTablename }
    }
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    const keyTypeError = validateJoinKeyColumnTypes(
      schemas,
      params.lefttable as WorkflowComponentValue,
      params.righttable as WorkflowComponentValue,
      resolvedParamValues.lefttablekeycolumn as string,
      resolvedParamValues.righttablekeycolumn as string,
      'lefttablekeycolumn'
    )
    if (keyTypeError) {
      problems.push(keyTypeError)
    }

    // Check if at least one column is selected from either table
    const columnSelectionError = validateAtLeastOneColumnSelected(
      resolvedParamValues.lefttablecolumns as string | null,
      resolvedParamValues.righttablecolumns as string | null,
      'lefttablecolumns'
    )
    if (columnSelectionError) {
      problems.push(columnSelectionError)
    }

    return problems
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  getVersion(provider: Provider) {
    return '1'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params, connection }: GetOutputSchemaParams): TableSchema {
    return buildJoinOutputSchema(
      columns.lefttable,
      columns.righttable,
      params.lefttablecolumns as string,
      params.righttablecolumns as string
    )
  }

  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { JoinV2 }
